import { useNavigate } from "react-router-dom";
import { Fragment, useState } from "react";

import moment from "moment";
import {
  Button,
  Chip,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import Box from "@mui/material/Box";
import { KeyboardArrowLeft } from "@mui/icons-material";

import conf from "config-dlm";
import { useAuth } from "components/auth/AuthProvider";
import { RequestSchema } from "hooks/user/base";
import LinearIndeterminate from "components/utils/LinearLoader";
import {
  AxiosMutationRequest,
  usePavmRequestDetail,
} from "hooks/pavm/requests";

interface TabPanelProps {
  index: number;
  value: number;
  children?: React.ReactNode;
}

export const a11yProps = (index: number) => {
  return {
    id: `request-tab-${index}`,
    "aria-controls": `request-tabpanel-${index}`,
  };
};

export const CustomTabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      id={`request-tabpanel-${index}`}
      role="requestpanel"
      hidden={value !== index}
      aria-labelledby={`request-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

export const PendingRow = (props: {
  reject: AxiosMutationRequest;
  approve: AxiosMutationRequest;
  request: RequestSchema;
  defaultOpen: boolean;
}) => {
  const {
    reject,
    approve,
    request: { id, requester, subject, project, updatedAt, uri, status },
    defaultOpen,
  } = props;

  const { user } = useAuth();
  const showEdit = user.role === "approver" || user.role === "admin";

  const [comment, setComment] = useState("");
  const [openDetail, setOpenDetail] = useState(defaultOpen);

  const detail = usePavmRequestDetail(uri, id);

  const toggleDetail = () => setOpenDetail(!openDetail);
  const handleReject = () => reject.mutate({ uri, id, project, comment });
  const handleApprove = () => approve.mutate({ uri, id, project, comment });

  const navigate = useNavigate();

  const handleNavigation = () => {
    if (window.history.state && window.history.state.idx > 0) {
      // go back to previous page (if exists)
      navigate(-1);
    } else {
      // otherwise, navigate to a specific page
      navigate("/requests/pending");
    }
  };

  const chip = <Chip color="warning" label={status} />;

  return (
    <Fragment>
      <TableRow key={id} sx={conf.LAST_CHILD_NO_BORDER}>
        <TableCell>
          <Button key="previous" color="inherit" onClick={handleNavigation}>
            <KeyboardArrowLeft />
          </Button>
        </TableCell>
        <TableCell>{chip}</TableCell>
        <TableCell align="right">{requester}</TableCell>
        <TableCell align="right">{subject}</TableCell>
        <TableCell align="right">
          <b>{project ?? "--"}</b>
        </TableCell>
        <TableCell align="right">{moment(updatedAt).fromNow()}</TableCell>
      </TableRow>

      {openDetail && (
        <TableRow key={`${id}-detail`} sx={conf.LAST_CHILD_NO_BORDER}>
          <TableCell colSpan={7}>
            <Box>
              <TextField
                multiline
                fullWidth
                aria-readonly
                margin="normal"
                InputProps={{ readOnly: true }}
                defaultValue={detail?.data?.text}
                sx={{
                  bgcolor: (theme) =>
                    theme.palette.mode === "light"
                      ? "primary.light"
                      : "primary.dark",
                  "&:last-child td, &:last-child th": { border: 0 },
                }}
              />

              {showEdit && (
                <TextField
                  size="small"
                  label="Comment (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  fullWidth
                  margin="normal"
                />
              )}

              {showEdit && (
                <Box my={1} textAlign="end">
                  <Button
                    size="small"
                    color="success"
                    variant="contained"
                    onClick={handleApprove}
                  >
                    Approve
                  </Button>

                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    onClick={handleReject}
                    style={{ marginLeft: "8px" }}
                  >
                    Reject
                  </Button>
                </Box>
              )}
            </Box>
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
};

export const ClosedRow = (props: {
  request: RequestSchema;
  defaultOpen: boolean;
}) => {
  const { id, status, subject, project, approver, requester, uri, updatedAt } =
    props.request;

  const navigate = useNavigate();

  const handleNavigation = () => {
    if (window.history.state && window.history.state.idx > 0) {
      // go back to previous page (if exists)
      navigate(-1);
    } else {
      // otherwise, navigate to a specific page
      navigate("/requests/pending");
    }
  };

  const chip =
    status === "APPROVED" ? (
      <Chip color="success" label={status} />
    ) : (
      <Chip color="error" label={status} />
    );

  return (
    <Fragment>
      <TableRow key={id} sx={conf.LAST_CHILD_NO_BORDER}>
        <TableCell>
          <Button key="previous" color="inherit" onClick={handleNavigation}>
            <KeyboardArrowLeft />
          </Button>
        </TableCell>
        <TableCell>{chip}</TableCell>
        <TableCell align="right">{requester ?? "--"}</TableCell>
        <TableCell align="right">{approver ?? "--"}</TableCell>
        <TableCell align="right">{subject ?? "--"}</TableCell>
        <TableCell align="right">
          <b>{project ?? "--"}</b>
        </TableCell>
        <TableCell align="right">
          {new Date(updatedAt).toLocaleString() ?? "--"}
        </TableCell>
      </TableRow>

      <RequestDetail id={id} uri={uri} />
    </Fragment>
  );
};

export const RequestDetail = (props: { id: string; uri: string }) => {
  const { id, uri } = props;
  const detail = usePavmRequestDetail(uri, id);

  return (
    <TableRow key={`${id}-detail`} sx={conf.LAST_CHILD_NO_BORDER}>
      {detail.isLoading ? (
        <TableCell colSpan={7}>
          <LinearIndeterminate />
        </TableCell>
      ) : detail.isError ? (
        <TableCell colSpan={7} align="center">
          {detail.error.response?.data.message}
        </TableCell>
      ) : (
        <TableCell colSpan={7}>
          <Box>
            <TextField
              multiline
              fullWidth
              aria-readonly
              margin="normal"
              label="Detail"
              InputProps={{ readOnly: true }}
              defaultValue={detail?.data?.text}
            />

            <TextField
              size="small"
              label="Comment"
              InputProps={{ readOnly: true }}
              defaultValue={detail?.data?.comment}
              fullWidth
              margin="normal"
            />
          </Box>
        </TableCell>
      )}
    </TableRow>
  );
};

export const PendingTableHeader = () => (
  <TableHead>
    <TableRow>
      <TableCell />
      <TableCell>
        <b>Status</b>
      </TableCell>
      <TableCell align="right">
        <b>Requester</b>
      </TableCell>
      <TableCell align="right">
        <b>Subject</b>
      </TableCell>
      <TableCell align="right">
        <b>Project</b>
      </TableCell>
      <TableCell align="right">
        <b>Issued</b>
      </TableCell>
    </TableRow>
  </TableHead>
);

export const ClosedTableHeader = () => (
  <TableHead>
    <TableRow>
      <TableCell />
      <TableCell>
        <b>Status</b>
      </TableCell>
      <TableCell align="right">
        <b>Requester</b>
      </TableCell>
      <TableCell align="right">
        <b>Approver</b>
      </TableCell>
      <TableCell align="right">
        <b>Subject</b>
      </TableCell>
      <TableCell align="right">
        <b>Project</b>
      </TableCell>
      <TableCell align="right">
        <b>Issued</b>
      </TableCell>
    </TableRow>
  </TableHead>
);
